import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getExternalAssessmentQuestions, submitExternalAssessment, searchNationalOrganizations } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  ClipboardCheck, 
  ChevronRight, 
  ChevronLeft,
  CheckCircle,
  Shield,
  AlertTriangle,
  Building2,
  User,
  Mail,
  MapPin,
  Calendar,
  Search,
  Loader2,
  ExternalLink,
  ArrowRight
} from 'lucide-react';
import debounce from 'lodash/debounce';

// US States list
const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
  'Wisconsin', 'Wyoming'
];

export default function ExternalAssessment() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Form steps
  const [currentStep, setCurrentStep] = useState(0);
  const steps = ['Organization Info', 'Contact Info', 'Affiliation', 'Assessment', 'Review'];
  
  // Form data
  const [formData, setFormData] = useState({
    organization_name: '',
    organization_type: searchParams.get('type') || 'sports',
    state: '',
    city: '',
    zip_code: '',
    address: '',
    admin_name: '',
    admin_email: '',
    admin_title: '',
    admin_phone: '',
    national_affiliation: '',
    state_affiliation: '',
    gl_policy_renewal_date: '',
    answers: []
  });
  
  // Assessment
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Affiliation search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  
  // Results
  const [assessmentResult, setAssessmentResult] = useState(null);

  // Load questions
  useEffect(() => {
    const loadQuestions = async () => {
      setLoading(true);
      try {
        const response = await getExternalAssessmentQuestions(formData.organization_type);
        setQuestions(response.data);
        // Initialize answers array
        setFormData(prev => ({
          ...prev,
          answers: response.data.map(q => ({ question_id: q.id, answer: null }))
        }));
      } catch (error) {
        toast.error('Failed to load assessment questions');
      } finally {
        setLoading(false);
      }
    };
    loadQuestions();
  }, [formData.organization_type]);

  // Debounced search for national organizations
  const debouncedSearch = useCallback(
    debounce(async (query) => {
      if (query.length < 2) {
        setSearchResults([]);
        return;
      }
      setSearching(true);
      try {
        const response = await searchNationalOrganizations(query, formData.organization_type);
        setSearchResults(response.data);
      } catch (error) {
        console.error('Search failed:', error);
      } finally {
        setSearching(false);
      }
    }, 300),
    [formData.organization_type]
  );

  useEffect(() => {
    debouncedSearch(searchQuery);
  }, [searchQuery, debouncedSearch]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAnswerChange = (questionId, answer) => {
    setFormData(prev => ({
      ...prev,
      answers: prev.answers.map(a => 
        a.question_id === questionId ? { ...a, answer } : a
      )
    }));
  };

  const validateStep = () => {
    switch (currentStep) {
      case 0: // Organization Info
        if (!formData.organization_name || !formData.state || !formData.city) {
          toast.error('Please fill in all required fields');
          return false;
        }
        return true;
      case 1: // Contact Info
        if (!formData.admin_name || !formData.admin_email || !formData.gl_policy_renewal_date) {
          toast.error('Please fill in all required fields');
          return false;
        }
        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.admin_email)) {
          toast.error('Please enter a valid email address');
          return false;
        }
        return true;
      case 2: // Affiliation (optional)
        return true;
      case 3: // Assessment
        const unanswered = formData.answers.filter(a => a.answer === null);
        if (unanswered.length > 0) {
          toast.error('Please answer all questions');
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (!validateStep()) return;
    setCurrentStep(prev => Math.min(prev + 1, steps.length - 1));
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;
    
    setSubmitting(true);
    try {
      const response = await submitExternalAssessment(formData);
      setAssessmentResult(response.data);
      toast.success('Assessment submitted successfully!');
    } catch (error) {
      console.error('Submission error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit assessment');
    } finally {
      setSubmitting(false);
    }
  };

  const getRiskColor = (level) => {
    switch (level) {
      case 'low': return 'text-green-600 bg-green-50';
      case 'medium': return 'text-yellow-600 bg-yellow-50';
      case 'high': return 'text-orange-600 bg-orange-50';
      case 'critical': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  // If assessment is complete, show results
  if (assessmentResult) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 py-12 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Logo */}
          <div className="text-center mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_safetyguard-4/artifacts/97rz3phl_players-health-lockup-color.png"
              alt="Players Health"
              className="h-10 mx-auto"
            />
          </div>

          <Card className="shadow-xl">
            <CardHeader className="text-center border-b pb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <CardTitle className="text-2xl">Assessment Complete!</CardTitle>
              <CardDescription>
                Your organization has been registered and your assessment is complete.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6 space-y-6">
              {/* Score Display */}
              <div className="text-center p-6 bg-slate-50 rounded-xl">
                <p className="text-sm text-slate-500 mb-2">YOUR RISK SCORE</p>
                <div className={`text-5xl font-bold ${getRiskColor(assessmentResult.risk_level).split(' ')[0]}`}>
                  {assessmentResult.score}
                </div>
                <Badge className={`mt-2 ${getRiskColor(assessmentResult.risk_level)}`}>
                  {assessmentResult.risk_level.toUpperCase()} RISK
                </Badge>
              </div>

              {/* Organization Info */}
              <div className="p-4 bg-blue-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Building2 className="w-5 h-5 text-blue-600" />
                  <span className="font-semibold text-blue-900">{assessmentResult.organization_name}</span>
                </div>
                {assessmentResult.hierarchy_resolution?.parent_org_name && (
                  <p className="text-sm text-blue-700 ml-7">
                    Affiliated with: {assessmentResult.hierarchy_resolution.parent_org_name}
                  </p>
                )}
              </div>

              {/* Top Recommendations */}
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                  Top Recommendations
                </h3>
                <ul className="space-y-2">
                  {assessmentResult.recommendations.slice(0, 5).map((rec, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                      <ChevronRight className="w-4 h-4 mt-0.5 text-slate-400 flex-shrink-0" />
                      {rec}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Next Steps */}
              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="font-semibold mb-3">Next Steps</h3>
                <ol className="space-y-2">
                  {assessmentResult.next_steps.map((step, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-sm">
                      <span className="w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center flex-shrink-0 text-xs">
                        {idx + 1}
                      </span>
                      <span className="text-slate-600">{step}</span>
                    </li>
                  ))}
                </ol>
              </div>

              {/* Email Notice */}
              <Alert>
                <Mail className="w-4 h-4" />
                <AlertDescription>
                  We've sent an email to <strong>{formData.admin_email}</strong> with instructions 
                  to complete your account setup and access your full dashboard.
                </AlertDescription>
              </Alert>

              {/* CTA */}
              <div className="text-center pt-4">
                <p className="text-sm text-slate-500 mb-4">
                  Didn't receive the email? Check your spam folder or
                </p>
                <Button 
                  variant="outline"
                  onClick={() => window.location.reload()}
                >
                  Start New Assessment
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_safetyguard-4/artifacts/97rz3phl_players-health-lockup-color.png"
            alt="Players Health"
            className="h-10 mx-auto mb-4"
          />
          <h1 className="text-2xl font-bold text-slate-900">Safety Risk Assessment</h1>
          <p className="text-slate-600 mt-2">
            Complete this assessment to evaluate your organization's safety protocols and get personalized recommendations.
          </p>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            {steps.map((step, index) => (
              <div 
                key={step}
                className={`flex items-center ${index < steps.length - 1 ? 'flex-1' : ''}`}
              >
                <div className={`
                  w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                  ${index < currentStep ? 'bg-green-600 text-white' : 
                    index === currentStep ? 'bg-blue-600 text-white' : 
                    'bg-slate-200 text-slate-500'}
                `}>
                  {index < currentStep ? <CheckCircle className="w-5 h-5" /> : index + 1}
                </div>
                {index < steps.length - 1 && (
                  <div className={`flex-1 h-1 mx-2 ${index < currentStep ? 'bg-green-600' : 'bg-slate-200'}`} />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between text-xs text-slate-500">
            {steps.map((step, index) => (
              <span key={step} className={`${index === currentStep ? 'text-blue-600 font-medium' : ''}`}>
                {step}
              </span>
            ))}
          </div>
        </div>

        <Card className="shadow-xl">
          <CardContent className="pt-6">
            {/* Step 0: Organization Info */}
            {currentStep === 0 && (
              <div className="space-y-6" data-testid="step-organization-info">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold">Organization Information</h2>
                    <p className="text-sm text-slate-500">Tell us about your organization</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <Label htmlFor="org_name">Organization Name *</Label>
                    <Input
                      id="org_name"
                      value={formData.organization_name}
                      onChange={(e) => handleInputChange('organization_name', e.target.value)}
                      placeholder="Enter your organization's name"
                      data-testid="input-org-name"
                    />
                  </div>

                  <div>
                    <Label>Organization Type *</Label>
                    <Select
                      value={formData.organization_type}
                      onValueChange={(value) => handleInputChange('organization_type', value)}
                    >
                      <SelectTrigger data-testid="select-org-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sports">Sports Organization</SelectItem>
                        <SelectItem value="fitness">Fitness Facility</SelectItem>
                        <SelectItem value="professional_sports">Professional Sports Organization</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>State *</Label>
                      <Select
                        value={formData.state}
                        onValueChange={(value) => handleInputChange('state', value)}
                      >
                        <SelectTrigger data-testid="select-state">
                          <SelectValue placeholder="Select state" />
                        </SelectTrigger>
                        <SelectContent>
                          {US_STATES.map(state => (
                            <SelectItem key={state} value={state}>{state}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="city">City *</Label>
                      <Input
                        id="city"
                        value={formData.city}
                        onChange={(e) => handleInputChange('city', e.target.value)}
                        placeholder="City"
                        data-testid="input-city"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="zip">ZIP Code</Label>
                      <Input
                        id="zip"
                        value={formData.zip_code}
                        onChange={(e) => handleInputChange('zip_code', e.target.value)}
                        placeholder="ZIP Code"
                      />
                    </div>
                    <div>
                      <Label htmlFor="address">Street Address</Label>
                      <Input
                        id="address"
                        value={formData.address}
                        onChange={(e) => handleInputChange('address', e.target.value)}
                        placeholder="Street address"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 1: Contact Info */}
            {currentStep === 1 && (
              <div className="space-y-6" data-testid="step-contact-info">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <User className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold">Contact Information</h2>
                    <p className="text-sm text-slate-500">Who should we contact about this assessment?</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="admin_name">Your Name *</Label>
                      <Input
                        id="admin_name"
                        value={formData.admin_name}
                        onChange={(e) => handleInputChange('admin_name', e.target.value)}
                        placeholder="Full name"
                        data-testid="input-admin-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="admin_title">Title</Label>
                      <Input
                        id="admin_title"
                        value={formData.admin_title}
                        onChange={(e) => handleInputChange('admin_title', e.target.value)}
                        placeholder="e.g., Executive Director"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="admin_email">Email Address *</Label>
                    <Input
                      id="admin_email"
                      type="email"
                      value={formData.admin_email}
                      onChange={(e) => handleInputChange('admin_email', e.target.value)}
                      placeholder="you@organization.com"
                      data-testid="input-admin-email"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      We'll send your assessment results and account setup instructions here
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="admin_phone">Phone Number</Label>
                    <Input
                      id="admin_phone"
                      type="tel"
                      value={formData.admin_phone}
                      onChange={(e) => handleInputChange('admin_phone', e.target.value)}
                      placeholder="(555) 123-4567"
                    />
                  </div>

                  <div className="pt-4 border-t">
                    <Label htmlFor="gl_date" className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      General Liability Policy Renewal Date *
                    </Label>
                    <Input
                      id="gl_date"
                      type="date"
                      value={formData.gl_policy_renewal_date}
                      onChange={(e) => handleInputChange('gl_policy_renewal_date', e.target.value)}
                      data-testid="input-gl-date"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Your assessment renewal will align with your GL policy renewal date
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Affiliation */}
            {currentStep === 2 && (
              <div className="space-y-6" data-testid="step-affiliation">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold">Organizational Affiliation</h2>
                    <p className="text-sm text-slate-500">Are you affiliated with a national or state governing body? (Optional)</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <Label htmlFor="national_search">National Organization Affiliation</Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="national_search"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search for your national governing body..."
                        className="pl-10"
                        data-testid="input-national-search"
                      />
                      {searching && (
                        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-slate-400" />
                      )}
                    </div>
                    
                    {/* Search Results */}
                    {searchResults.length > 0 && (
                      <div className="mt-2 border rounded-lg overflow-hidden">
                        {searchResults.map((org) => (
                          <button
                            key={org.id}
                            onClick={() => {
                              handleInputChange('national_affiliation', org.name);
                              setSearchQuery(org.name);
                              setSearchResults([]);
                            }}
                            className="w-full px-4 py-3 text-left hover:bg-slate-50 flex items-center justify-between border-b last:border-b-0"
                          >
                            <div>
                              <p className="font-medium">{org.name}</p>
                              <p className="text-sm text-slate-500">{org.level} • {org.child_count} member orgs</p>
                            </div>
                            <Badge variant="outline">{Math.round(org.match_score * 100)}% match</Badge>
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Manual Entry Option */}
                    {searchQuery.length > 2 && searchResults.length === 0 && !searching && (
                      <div className="mt-2 p-4 bg-slate-50 rounded-lg">
                        <p className="text-sm text-slate-600">
                          No matching organizations found. You can enter the name manually:
                        </p>
                        <Input
                          className="mt-2"
                          value={formData.national_affiliation}
                          onChange={(e) => handleInputChange('national_affiliation', e.target.value)}
                          placeholder="Enter organization name"
                        />
                      </div>
                    )}

                    {formData.national_affiliation && (
                      <div className="mt-2 flex items-center gap-2 text-sm text-green-600">
                        <CheckCircle className="w-4 h-4" />
                        Selected: {formData.national_affiliation}
                        <button 
                          onClick={() => {
                            handleInputChange('national_affiliation', '');
                            setSearchQuery('');
                          }}
                          className="text-slate-400 hover:text-slate-600 ml-2"
                        >
                          Clear
                        </button>
                      </div>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="state_affiliation">State Association (Optional)</Label>
                    <Input
                      id="state_affiliation"
                      value={formData.state_affiliation}
                      onChange={(e) => handleInputChange('state_affiliation', e.target.value)}
                      placeholder="e.g., California Youth Soccer Association"
                    />
                  </div>

                  <Alert>
                    <Shield className="w-4 h-4" />
                    <AlertDescription>
                      Affiliating with a parent organization allows you to benefit from their policies, 
                      training programs, and aggregate risk insights while maintaining your own data privacy.
                    </AlertDescription>
                  </Alert>
                </div>
              </div>
            )}

            {/* Step 3: Assessment Questions */}
            {currentStep === 3 && (
              <div className="space-y-6" data-testid="step-assessment">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <ClipboardCheck className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold">Safety Assessment</h2>
                    <p className="text-sm text-slate-500">
                      Question {currentQuestion + 1} of {questions.length}
                    </p>
                  </div>
                </div>

                <Progress value={((currentQuestion + 1) / questions.length) * 100} className="h-2" />

                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                  </div>
                ) : questions.length > 0 && (
                  <div className="py-6">
                    <div className="mb-6">
                      <Badge variant="outline" className="mb-3">
                        {questions[currentQuestion]?.category}
                      </Badge>
                      <h3 className="text-lg font-medium mb-2">
                        {questions[currentQuestion]?.question}
                      </h3>
                      {questions[currentQuestion]?.description && (
                        <p className="text-sm text-slate-500">
                          {questions[currentQuestion].description}
                        </p>
                      )}
                    </div>

                    {questions[currentQuestion]?.type === 'yes_no' ? (
                      <RadioGroup
                        value={formData.answers.find(a => a.question_id === questions[currentQuestion].id)?.answer?.toString()}
                        onValueChange={(value) => handleAnswerChange(questions[currentQuestion].id, parseInt(value))}
                        className="space-y-3"
                      >
                        <div className="flex items-center space-x-3 p-4 border rounded-lg hover:bg-slate-50 cursor-pointer">
                          <RadioGroupItem value="1" id="yes" />
                          <Label htmlFor="yes" className="flex-1 cursor-pointer font-normal">Yes</Label>
                        </div>
                        <div className="flex items-center space-x-3 p-4 border rounded-lg hover:bg-slate-50 cursor-pointer">
                          <RadioGroupItem value="0" id="no" />
                          <Label htmlFor="no" className="flex-1 cursor-pointer font-normal">No</Label>
                        </div>
                      </RadioGroup>
                    ) : questions[currentQuestion]?.type === 'multiple_choice' ? (
                      <RadioGroup
                        value={formData.answers.find(a => a.question_id === questions[currentQuestion].id)?.answer}
                        onValueChange={(value) => handleAnswerChange(questions[currentQuestion].id, value)}
                        className="space-y-2"
                      >
                        {questions[currentQuestion].options?.map(option => (
                          <div key={option.value} className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-slate-50 cursor-pointer">
                            <RadioGroupItem value={option.value} id={option.value} />
                            <Label htmlFor={option.value} className="flex-1 cursor-pointer font-normal">
                              {option.label}
                            </Label>
                          </div>
                        ))}
                      </RadioGroup>
                    ) : null}

                    {/* Question Navigation */}
                    <div className="flex justify-between items-center mt-8 pt-6 border-t">
                      <Button
                        variant="outline"
                        onClick={() => setCurrentQuestion(prev => Math.max(0, prev - 1))}
                        disabled={currentQuestion === 0}
                      >
                        <ChevronLeft className="w-4 h-4 mr-2" />
                        Previous
                      </Button>
                      
                      <div className="flex gap-1">
                        {questions.map((_, idx) => (
                          <button
                            key={idx}
                            onClick={() => setCurrentQuestion(idx)}
                            className={`w-2 h-2 rounded-full transition-colors ${
                              idx === currentQuestion ? 'bg-blue-600' :
                              formData.answers[idx]?.answer !== null ? 'bg-green-500' : 'bg-slate-200'
                            }`}
                          />
                        ))}
                      </div>

                      <Button
                        onClick={() => {
                          if (currentQuestion < questions.length - 1) {
                            setCurrentQuestion(prev => prev + 1);
                          }
                        }}
                        disabled={currentQuestion === questions.length - 1}
                      >
                        Next
                        <ChevronRight className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Step 4: Review */}
            {currentStep === 4 && (
              <div className="space-y-6" data-testid="step-review">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold">Review & Submit</h2>
                    <p className="text-sm text-slate-500">Please review your information before submitting</p>
                  </div>
                </div>

                <div className="space-y-4">
                  {/* Organization Summary */}
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <h3 className="font-medium mb-3 flex items-center gap-2">
                      <Building2 className="w-4 h-4" />
                      Organization
                    </h3>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div><span className="text-slate-500">Name:</span> {formData.organization_name}</div>
                      <div><span className="text-slate-500">Type:</span> {formData.organization_type === 'sports' ? 'Sports Organization' : formData.organization_type === 'professional_sports' ? 'Professional Sports Organization' : 'Fitness Facility'}</div>
                      <div><span className="text-slate-500">Location:</span> {formData.city}, {formData.state}</div>
                      <div><span className="text-slate-500">GL Renewal:</span> {formData.gl_policy_renewal_date}</div>
                    </div>
                  </div>

                  {/* Contact Summary */}
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <h3 className="font-medium mb-3 flex items-center gap-2">
                      <User className="w-4 h-4" />
                      Contact
                    </h3>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div><span className="text-slate-500">Name:</span> {formData.admin_name}</div>
                      <div><span className="text-slate-500">Email:</span> {formData.admin_email}</div>
                      {formData.admin_title && <div><span className="text-slate-500">Title:</span> {formData.admin_title}</div>}
                      {formData.admin_phone && <div><span className="text-slate-500">Phone:</span> {formData.admin_phone}</div>}
                    </div>
                  </div>

                  {/* Affiliation Summary */}
                  {(formData.national_affiliation || formData.state_affiliation) && (
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <h3 className="font-medium mb-3 flex items-center gap-2">
                        <Shield className="w-4 h-4" />
                        Affiliations
                      </h3>
                      <div className="text-sm space-y-1">
                        {formData.national_affiliation && (
                          <div><span className="text-slate-500">National:</span> {formData.national_affiliation}</div>
                        )}
                        {formData.state_affiliation && (
                          <div><span className="text-slate-500">State:</span> {formData.state_affiliation}</div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Assessment Summary */}
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <h3 className="font-medium mb-3 flex items-center gap-2">
                      <ClipboardCheck className="w-4 h-4" />
                      Assessment
                    </h3>
                    <div className="text-sm">
                      <span className="text-slate-500">Questions Answered:</span> {formData.answers.filter(a => a.answer !== null).length} of {questions.length}
                    </div>
                  </div>

                  <Alert>
                    <Mail className="w-4 h-4" />
                    <AlertDescription>
                      After submission, you'll receive an email at <strong>{formData.admin_email}</strong> with 
                      your assessment results and instructions to set up your account.
                    </AlertDescription>
                  </Alert>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between items-center mt-8 pt-6 border-t">
              <Button
                variant="outline"
                onClick={handleBack}
                disabled={currentStep === 0}
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Back
              </Button>

              {currentStep < steps.length - 1 ? (
                <Button onClick={handleNext} data-testid="btn-next">
                  Continue
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button 
                  onClick={handleSubmit} 
                  disabled={submitting}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="btn-submit"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      Submit Assessment
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-slate-500">
          <p>Already have an account? <a href="/login" className="text-blue-600 hover:underline">Log in</a></p>
          <p className="mt-2">© 2026 Players Health. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}
